import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  ProjectResource,
  ReportProgress,
  ReportProgressStatus,
} from "../../../types/project";
import { ucFirstAllWords } from "../../../utils/helper";
import BaseModal from "../../../components/global/BaseModal";

const LogModal = ({
  open,
  onClose,
  job,
}: {
  open: boolean;
  onClose: () => void;
  job: ProjectResource | null;
}) => {
  const sorted = React.useMemo(
    () =>
      job?.report_progresses
        ?.slice()
        .sort(
          (a: ReportProgress, b: ReportProgress) =>
            new Date(a.check_in).getTime() - new Date(b.check_in).getTime()
        ),
    [job]
  );

  const footer = <Button onClick={onClose}>Close</Button>;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Work Activity Log"
      footer={footer}
      maxWidth="md"
    >
      <Box
        sx={{
          p: 3,
          display: "grid",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`Hired ${dayjs(job?.hired_at).format("MMM DD, YYYY hh:mm A z")}`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, px: 1.5 }}
          />
        </Stack>
        <Divider flexItem sx={{ opacity: 0.4 }} />
        <Stack spacing={2} sx={{ maxHeight: 420, overflowY: "auto", pr: 1 }}>
          {sorted?.map((it) => {
            const range = `${dayjs(it.check_in).format("MMM DD, YYYY hh:mm A z")} → ${
              it.check_out ? dayjs(it.check_out).format("MMM DD, YYYY hh:mm A z") : "-"
            }`;
            let statusLabel = "";
            if (it.status === ReportProgressStatus.SUBMITTED) {
              statusLabel = "Work Submitted";
            } else if (it.status === ReportProgressStatus.APPROVED) {
              statusLabel = "Work Approved";
            } else if (it.status === ReportProgressStatus.REJECTED) {
              statusLabel = "Work Rejected";
            }
            const hasReason = Boolean(it.reason);

            return (
              <Box
                key={`${it.id}-${it.check_in}`}
                sx={{
                  borderRadius: 3,
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                  backdropFilter: "blur(12px)",
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {range}
                </Typography>
                {statusLabel && (
                  <Chip
                    label={statusLabel}
                    size="small"
                    sx={{ mt: 1, fontWeight: 500 }}
                    color={
                      it.status === ReportProgressStatus.APPROVED
                        ? "success"
                        : it.status === ReportProgressStatus.SUBMITTED
                        ? "info"
                        : "error"
                    }
                    variant="outlined"
                  />
                )}
                {hasReason && (
                  <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
                    {ucFirstAllWords(it.reason)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </BaseModal>
  );
};
export default LogModal;
